import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";

import LeaveList from "../components/hr/LeaveList";
import PageLayout from "../components/PageLayout";
import { HRPageProps } from "../components/common/types";
import { useAuth } from "../context/AuthContext";
import { getAllLeavesByTenant, updateLeaveStatus } from "../api/leave";

interface LeaveRecord {
  id: string;
  tenant_id: number;
  employee_id: number;
  employee_name: string;
  leave_date: string;
  leave_type: string;
  reason: string;
  status: "Pending" | "Approved" | "Rejected";
  approved_by?: string;
  created_at?: string;
}

const normalizeStatus = (s: string): "Pending" | "Approved" | "Rejected" => {
  const lower = s?.toLowerCase();
  if (lower === "approved") return "Approved";
  if (lower === "rejected") return "Rejected";
  return "Pending";
};

const HRPage: React.FC<HRPageProps> = ({ onLogout, onNavigate }) => {
  const { user } = useAuth();
  const tenantId = (user as any)?.tenant_id as number;

  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getAllLeavesByTenant(tenantId);
      const raw: any[] = res.data?.data ?? res.data ?? [];
      const mapped: LeaveRecord[] = raw.map((l) => ({
        id: String(l.id),
        tenant_id: l.tenant_id,
        employee_id: l.employee_id,
        employee_name: l.employee_name ?? "Unknown",
        leave_date: l.leave_date,
        leave_type: l.leave_type ?? "",
        reason: l.reason ?? "",
        status: normalizeStatus(l.status),
        approved_by: l.approved_by,
        created_at: l.created_at,
      }));
      setLeaves(mapped);
    } catch (err) {
      console.error("Failed to load leaves:", err);
      setError("Failed to load leave data. Please try again.");
    }
    setIsLoading(false);
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusUpdate = async (
    record: LeaveRecord,
    newStatus: string,
    _comments: string
  ) => {
    try {
      const backendStatus = newStatus === "Approved" ? "approved" : "rejected";
      const approvedById = (user as any)?.id;
      await updateLeaveStatus(tenantId, Number(record.id), backendStatus, approvedById);
      await loadData();
    } catch (err) {
      console.error("Failed to update leave status:", err);
    }
  };

  const getFiltered = (status: string) =>
    leaves.filter((l) => l.status.toLowerCase() === status.toLowerCase());

  const pendingCount = getFiltered("pending").length;
  const approvedCount = getFiltered("approved").length;
  const rejectedCount = getFiltered("rejected").length;

  const tabLabels: Record<string, string> = {
    pending: `Pending (${pendingCount})`,
    approved: `Approved (${approvedCount})`,
    rejected: `Rejected (${rejectedCount})`,
  };

  const toLeaveRequest = (r: LeaveRecord) => ({
    id: r.id,
    leave_type: r.leave_type,
    start_date: r.leave_date,
    end_date: r.leave_date,
    total_days: 1,
    reason: r.reason,
    status: r.status,
    created_by: r.employee_name,
    approved_by: r.approved_by,
    created_date: r.created_at,
    employee_name: r.employee_name,
  });

  return (
    <PageLayout onLogout={onLogout} currentPage="hr-leave" onNavigate={onNavigate}>
      <div className="p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">HR &amp; Leave Management</h1>
                <p className="text-blue-600 mt-1">Review and manage employee leave requests</p>
              </div>
              <button
                onClick={loadData}
                disabled={isLoading}
                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                {(["pending", "approved", "rejected"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors capitalize ${
                      activeTab === tab
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tabLabels[tab]}
                  </button>
                ))}
              </div>
            </div>

            <LeaveList
              requests={getFiltered(activeTab).map(toLeaveRequest) as any}
              isLoading={isLoading}
              userRole="admin"
              onStatusUpdate={(req, status, comments) =>
                handleStatusUpdate(
                  leaves.find((l) => l.id === req.id)!,
                  status,
                  comments
                )
              }
            />
          </div>
        </div>
    </PageLayout>
  );
};

export default HRPage;
